/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'

/**
 * Import async components.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-section' */'~async/accordion-section/accordion-section') }),
    'affirm-messaging': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.affirm-messaging' */'~async/affirm-messaging/affirm-messaging') }),
    'article-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.article-carousel' */'~async/article-carousel/article-carousel') }),
    'blog-listing-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.blog-listing-page' */'~async/blog-listing-page/blog-listing-page') }),
    'cart-drawer-overlay': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer-overlay' */'~async/cart-drawer/cart-drawer-overlay') }),
    'collection-sticky-header': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-sticky-header' */'~async/collection-sticky-header/collection-sticky-header') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-edit': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-edit' */'~async/customers/edit/customers-edit') }),
    'customers-favorites': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-favorites' */'~async/customers/favorites/customers-favorites') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'customers-settings': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-settings' */'~async/customers/customers-settings/customers-settings') }),
    'dickey-bundle': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.dickey-bundle' */'~async/dickey-bundle/dickey-bundle') }),
    'email-registration-gates': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.email-registration-gates' */'~async/email-registration-gates/email-registration-gates') }),
    'email-sign-up': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.email-sign-up' */'~async/email-sign-up/email-sign-up') }),
    'fifty-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.fifty-banner' */'~async/fifty-banner/fifty-banner') }),
    'five-blocks-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.five-blocks-section' */'~async/five-blocks-section/five-blocks-section') }),
    'hero-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-banner' */'~async/hero-banner/hero-banner') }),
    'highlight-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.highlight-blog' */'~async/highlight-blog/highlight-blog') }),
    'image-gallery': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-gallery' */'~async/image-gallery/image-gallery') }),
    'image-gallery-grid': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-gallery-grid' */'~async/image-gallery-grid/image-gallery-grid') }),
    'image-text-overlay': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-text-overlay' */'~async/image-text-overlay/image-text-overlay') }),
    'kustomer-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.kustomer-form' */'~async/kustomer-form/kustomer-form') }),
    'lookbook-images': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.lookbook-images' */'~async/lookbook-images/lookbook-images') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'media-text-split': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.media-text-split' */'~async/media-text-split/media-text-split') }),
    'multi-column': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-column' */'~async/multi-column/multi-column') }),
    'multi-row': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-row' */'~async/multi-row/multi-row') }),
    'multi-store-modal': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-store-modal' */'~async/multi-store-modal/multi-store-modal') }),
    'product-quality-notes': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-quality-notes' */'~async/product-quality-notes/product-quality-notes') }),
    'product-recommendations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-recommendations' */'~async/product-recommendations/product-recommendations') }),
    'promotional-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promotional-banner' */'~async/promotional-banner/promotional-banner') }),
    'rich-text-editor': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.rich-text-editor' */'~async/rich-text-editor/rich-text-editor') }),
    'search-drawer-overlay': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.search-drawer-overlay' */'~async/search-drawer/search-drawer-overlay') }),
    'shopping-gives-modal': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.shopping-gives-modal' */'~async/shopping-gives-modal/shopping-gives-modal') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'size-guide-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.size-guide-page' */'~async/size-guide-page/size-guide-page') }),
    'store-locator': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.store-locator' */'~async/store-locator/store-locator') }),
    'text-navigation': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-navigation' */'~async/text-navigation/text-navigation') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import AccordionSnippet from '~global/accordion-snippet/accordion-snippet'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import DynamicYield from '~global/dynamic-yield/dynamic-yield'
import Index from '~global/core/index'
import LanguageSwitcher from '~global/language-switcher/language-switcher'
import MultiStore from '~global/multi-store/multi-store'
import Navigation from '~global/site-header-group/navigation/navigation'
import Overlay from '~global/overlay/overlay'
import ProductBadge from '~global/product-badge/product-badge'
import ProductCard from '~global/product-card/product-card'
import ProductCarousel from '~global/product-carousel/product-carousel'
import ProductFormDrawer from '~global/product-form-drawer/product-form-drawer'
import ProductPrices from '~global/product-prices/product-prices'
import ProductSwatches from '~global/product-swatches/product-swatches'
import PromoBanner from '~global/site-header-group/promo-banner/promo-banner'
import ResponsiveImage from '~global/images/responsive-image'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
import SizeGuide from '~global/size-guide/size-guide'
import SizeGuideDrawer from '~global/size-guide/size-guide-drawer'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'accordion-snippet': AccordionSnippet,
    'art-direction': ArtDirection,
    'btn': Btn,
    'dynamic-yield': DynamicYield,
    'index': Index,
    'language-switcher': LanguageSwitcher,
    'multi-store': MultiStore,
    'navigation': Navigation,
    'overlay': Overlay,
    'product-badge': ProductBadge,
    'product-card': ProductCard,
    'product-carousel': ProductCarousel,
    'product-form-drawer': ProductFormDrawer,
    'product-prices': ProductPrices,
    'product-swatches': ProductSwatches,
    'promo-banner': PromoBanner,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    'size-guide': SizeGuide,
    'size-guide-drawer': SizeGuideDrawer,
    // canvas-global-object-end
  }
}

/**
 * Import web components.
 * - Must be manually updated with new async web components (components/async).
 * - Use component command import to update.
 */
// canvas-web-import-start
// canvas-web-import-end

/**
 * Import Vuex stores.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import DynamicYieldStore from '~stores/dynamic-yield'
import IndexStore from '~stores/index'
import MultiStoreStore from '~stores/multi-store'
import OverlaysStore from '~stores/overlays'
import ProductFormDrawerStore from '~stores/product-form-drawer'
import QuickAddToCartStore from '~stores/quick-add-to-cart'
import ShopTheLookStore from '~stores/shop-the-look'
import SizeGuideDrawerStore from '~stores/size-guide-drawer'
import DickeyBundleStore from '~stores/dickey-bundle'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'dynamic-yield': DynamicYieldStore,
    'index': IndexStore,
    'multi-store': MultiStoreStore,
    'overlays': OverlaysStore,
    'productFormDrawer': ProductFormDrawerStore,
    'quickAddToCart': QuickAddToCartStore,
    'shopTheLook': ShopTheLookStore,
    'sizeGuideDrawer': SizeGuideDrawerStore,
    'dickeyBundle': DickeyBundleStore,
    // canvas-stores-object-end
  }
}
